import { CalendarEvent } from '@/services/calendarService';
import { CALENDAR_STANDARD_FIELD_OPTIONS } from './calendarFieldOptions';

/** 12-hour time matching backend format_calendar_time_12h (e.g. 6:00 am, 8:30 pm). */
export function formatCalendarTime12h(date: Date): string {
  const hour24 = date.getHours();
  const hour12 = hour24 % 12 || 12;
  const minute = date.getMinutes();
  const period = hour24 < 12 ? 'am' : 'pm';
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
}

/** Date as DD/MM/YYYY matching backend calendar_processor. */
export function formatCalendarDate(date: Date): string {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

const RESERVED_DESC_KEYS = new Set([
  'summary', 'start_date', 'start_time', 'location', 'meeting_link', 'description', '1', '2', '3',
]);

/**
 * Resolve meeting / event URL for template preview.
 * Priority: hangoutLink → conference video entryPoint → first entryPoint uri → htmlLink.
 */
export function extractMeetingLink(event: CalendarEvent): string {
  const hangout = (event.hangoutLink || '').trim();
  if (hangout) return hangout;

  const entryPoints = event.conferenceData?.entryPoints || [];
  const video = entryPoints.find(
    (ep) => (ep.entryPointType || '').toLowerCase() === 'video' && (ep.uri || '').trim(),
  );
  if (video?.uri?.trim()) return video.uri.trim();

  const withUri = entryPoints.find((ep) => (ep.uri || '').trim());
  if (withUri?.uri?.trim()) return withUri.uri.trim();

  return (event.htmlLink || '').trim();
}

/** Parse key:value lines from a Google Calendar event description. */
export function extractDescriptionVars(description: string): Record<string, string> {
  const result: Record<string, string> = {};
  const cleanDesc = (description || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>?/gm, '\n');
  for (const line of cleanDesc.split('\n')) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;
    const key = line.slice(0, colonIdx).trim().toLowerCase();
    const value = line.slice(colonIdx + 1).trim();
    if (!key || !value || /^\d+$/.test(key) || RESERVED_DESC_KEYS.has(key)) continue;
    result[key] = value;
  }
  return result;
}

function parseEventStartIso(event: CalendarEvent): string {
  return event.start?.dateTime || event.start?.date || "";
}

/** Pick the event with the latest start date/time. */
export function pickNewestCalendarEvent(
  eventList: CalendarEvent[],
): CalendarEvent | null {
  if (!eventList.length) return null;
  return eventList.reduce((newest, current) => {
    const newestStart = parseEventStartIso(newest);
    const currentStart = parseEventStartIso(current);
    return currentStart > newestStart ? current : newest;
  });
}

function parseEventStart(event: CalendarEvent): Date | null {
  const raw = parseEventStartIso(event);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Build preview/send-compatible row_data from a calendar event.
 * Returns null when no event is provided — no fake default values.
 */
export function buildCalendarSampleRowFromEvent(
  event: CalendarEvent | null | undefined,
): Record<string, string> | null {
  if (!event) return null;

  const eventStart = parseEventStart(event);
  const hasDateTime = Boolean(event.start?.dateTime);

  const dateStr = eventStart ? formatCalendarDate(eventStart) : '';
  const timeStr = eventStart
    ? hasDateTime
      ? formatCalendarTime12h(eventStart)
      : 'Todo el día'
    : '';

  const descriptionVars = extractDescriptionVars(event.description || '');
  const meetingLink = extractMeetingLink(event);

  const row: Record<string, string> = {
    summary: event.summary || '',
    start_date: dateStr,
    start_time: timeStr,
    location: event.location || '',
    meeting_link: meetingLink,
    description: event.description || '',
    '1': dateStr,
    '2': timeStr,
    '3': event.location || '',
    ...descriptionVars,
  };

  for (const { key, label } of CALENDAR_STANDARD_FIELD_OPTIONS) {
    row[label] = row[key] ?? '';
  }

  return row;
}

/** Resolve a preview value from column_mapping label → sample row. */
export function resolveCalendarPreviewValue(
  columnLabel: string,
  sampleRow: Record<string, string>,
): string | undefined {
  if (!columnLabel?.trim()) return undefined;
  if (sampleRow[columnLabel] !== undefined) return String(sampleRow[columnLabel]);
  const lower = columnLabel.toLowerCase().trim();
  const match = Object.keys(sampleRow).find((k) => k.toLowerCase() === lower);
  return match !== undefined ? String(sampleRow[match]) : undefined;
}

/** Replace {{N}} placeholders using column_mapping labels and sample row values. */
export function applyCalendarColumnMappingPreview(
  text: string,
  columnMapping: Record<string, string>,
  sampleRow: Record<string, string>,
): string {
  let result = text;
  for (const [varNum, columnLabel] of Object.entries(columnMapping)) {
    if (!columnLabel?.trim()) continue;
    const value = resolveCalendarPreviewValue(columnLabel, sampleRow);
    if (value === undefined) continue;
    const regex = new RegExp(`\\{\\{\\s*${varNum}\\s*\\}\\}`, 'g');
    result = result.replace(regex, value);
  }
  return result;
}
